import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Crown, Route } from 'lucide-react';
import { UserProfile } from './UserProfile';
import { ProfessionalSubscriptionDetails } from './ProfessionalSubscriptionDetails';
import { SubscriptionFallback } from './SubscriptionFallback';
import { LocationSelectionView } from './LocationSelectionView';
import { MilkyWayPlannerView } from './MilkyWayPlannerView';
import { MoonPlannerView } from './MoonPlannerView';
import { TripListView } from './TripListView';
import { TripDetailView } from './TripDetailView';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { getRewardfulReferral } from '@/utils/rewardful';
import { redirectToCheckout } from '@/lib/analytics';
import { TravelItineraryWithStops } from '@/hooks/useTravelItineraries';

interface DashboardLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  notes?: string | null;
  created_at?: string;
  bortle_class?: number | null;
  display_name?: string;
}

interface TabContentProps {
  activeTab: string;
  selectedLocation: DashboardLocation | null;
  subscriptionData: unknown;
  onLocationSelect: (location: DashboardLocation | null) => void;
  onManageSubscription: () => void;
  onDetailViewChange?: (isInDetailView: boolean) => void;
}

export const TabContent: React.FC<TabContentProps> = ({
  activeTab,
  selectedLocation,
  subscriptionData,
  onLocationSelect,
  onManageSubscription,
  onDetailViewChange
}) => {
  const { isSubscribed, loading, subscriptionLoading, subscriptionData: authSubscriptionData } = useAuth();
  const [milkyWayLocation, setMilkyWayLocation] = useState<DashboardLocation | null>(null);
  const [moonLocation, setMoonLocation] = useState<DashboardLocation | null>(null);
  const [selectedTrip, setSelectedTrip] = useState<TravelItineraryWithStops | null>(null);

  // Check if user has access to premium features
  // Allow access if isSubscribed is true OR subscription_status is 'active'
  // This handles cases where the edge function call fails but the user is subscribed
  const hasActivePremiumAccess = isSubscribed || authSubscriptionData?.subscription_status === 'active';
  const hasCanceledSubscription = authSubscriptionData?.subscription_status &&
    ['canceled', 'incomplete', 'past_due', 'unpaid'].includes(authSubscriptionData.subscription_status);

  const handleUpgrade = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { referralId: getRewardfulReferral() }
      });
      if (error) return;
      if (data?.url) {
        redirectToCheckout(data.url, 'Premium');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
    }
  };

  // Report detail view state changes to parent
  React.useEffect(() => {
    const isInDetailView = !!milkyWayLocation || !!moonLocation || !!selectedTrip;
    onDetailViewChange?.(isInDetailView);
  }, [milkyWayLocation, moonLocation, selectedTrip, onDetailViewChange]);

  // Profile tab content
  if (activeTab === 'profile') {
    return (
      <div className="space-y-6">
        <UserProfile />
      </div>
    );
  }

  // Subscription tab content
  if (activeTab === 'subscription') {
    return (
      <div className="space-y-6">
        {loading || subscriptionLoading ? (
          <SubscriptionFallback type="loading" />
        ) : !authSubscriptionData?.subscription_status ? (
          <SubscriptionFallback type="not-subscribed" />
        ) : hasCanceledSubscription ? (
          <SubscriptionFallback type="canceled" onManageSubscription={onManageSubscription} />
        ) : (
          <ProfessionalSubscriptionDetails 
            onManageSubscription={onManageSubscription}
          />
        )}
      </div>
    );
  }

  const handleTripSelect = (trip: TravelItineraryWithStops) => {
    setSelectedTrip(trip);
  };

  const handleBackToTrips = () => {
    setSelectedTrip(null);
  };

  // While subscription status is loading, show skeleton instead of upsell
  const isCheckingAccess = subscriptionLoading && !hasActivePremiumAccess;

  // Trips tab content
  if (activeTab === 'trips') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-border">
          <Route className="w-5 h-5 text-gold" />
          <div>
            <h2 className="text-lg font-semibold text-foreground">Trip Planner</h2>
            <p className="text-sm text-muted-foreground">Build multi-stop photography itineraries</p>
          </div>
        </div>
        {isCheckingAccess ? (
          <div className="text-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : !hasActivePremiumAccess ? (
          <div className="text-center p-8 bg-card rounded-lg border">
            <h3 className="text-lg font-semibold mb-2">Premium Feature</h3>
            <p className="text-muted-foreground mb-4">
              {hasCanceledSubscription
                ? "Your subscription has been canceled. Please renew to access the Travel Planner."
                : "The Travel Planner is available to premium subscribers only."
              }
            </p>
            {hasCanceledSubscription ? (
              <button
                onClick={onManageSubscription}
                className="px-4 py-2 bg-foreground text-background rounded-md hover:bg-foreground/90"
              >
                Manage Subscription
              </button>
            ) : (
              <Button
                onClick={handleUpgrade}
                variant="premium"
              >
                <Crown className="w-4 h-4 mr-2" />
                Get Premium
              </Button>
            )}
          </div>
        ) : selectedTrip ? (
          <TripDetailView
            trip={selectedTrip}
            onBack={handleBackToTrips}
          />
        ) : (
          <TripListView onSelectTrip={handleTripSelect} />
        )}
      </div>
    );
  }

  // Moon planner tab content
  if (activeTab === 'moon') {
    return (
      <div className="space-y-6">
        {isCheckingAccess ? (
          <div className="text-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : !hasActivePremiumAccess ? (
          <div className="text-center p-8 bg-card rounded-lg border">
            <h3 className="text-lg font-semibold mb-2">Premium Feature</h3>
            <p className="text-muted-foreground mb-4">
              {hasCanceledSubscription
                ? "Your subscription has been canceled. Please renew to access the Moon Planner."
                : "The Moon Planner is available to premium subscribers only."
              }
            </p>
            {hasCanceledSubscription ? (
              <button
                onClick={onManageSubscription}
                className="px-4 py-2 bg-foreground text-background rounded-md hover:bg-foreground/90"
              >
                Manage Subscription
              </button>
            ) : (
              <Button
                onClick={handleUpgrade}
                variant="premium"
              >
                <Crown className="w-4 h-4 mr-2" />
                Get Premium
              </Button>
            )}
          </div>
        ) : moonLocation ? (
          <MoonPlannerView
            selectedLocation={moonLocation}
            onBackToSelection={() => setMoonLocation(null)}
          />
        ) : (
          <LocationSelectionView
            onLocationSelect={(loc) => setMoonLocation(loc)}
            plannerType="moon"
          />
        )}
      </div>
    );
  }

  // Default: Milky Way planner tab content
  return (
    <div className="space-y-6">
      {isCheckingAccess ? (
        <div className="text-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      ) : !hasActivePremiumAccess ? (
        <div className="text-center p-8 bg-card rounded-lg border">
          <h3 className="text-lg font-semibold mb-2">Premium Feature</h3>
          <p className="text-muted-foreground mb-4">
            {hasCanceledSubscription
              ? "Your subscription has been canceled. Please renew to access the Milky Way Planner."
              : "The Milky Way Planner is available to premium subscribers only."
            }
          </p>
          {hasCanceledSubscription ? (
            <button
              onClick={onManageSubscription}
              className="px-4 py-2 bg-foreground text-background rounded-md hover:bg-foreground/90"
            >
              Manage Subscription
            </button>
          ) : (
            <Button
              onClick={handleUpgrade}
              variant="premium"
            >
              <Crown className="w-4 h-4 mr-2" />
              Get Premium
            </Button>
          )}
        </div>
      ) : milkyWayLocation ? (
        <MilkyWayPlannerView
          selectedLocation={milkyWayLocation}
          onLocationSelect={onLocationSelect}
          onBackToSelection={() => setMilkyWayLocation(null)}
        />
      ) : (
        <LocationSelectionView
          onLocationSelect={(loc) => setMilkyWayLocation(loc)}
          plannerType="milkyway"
        />
      )}
    </div>
  );
};
